/* eslint-disable @typescript-eslint/indent */
import { productCategoriesMap } from '@constants/productType';

import { HotSpotHotelApi, HotelApiAlgolia } from '@customTypes/hotel-api';
import { SearchState } from '@customTypes/search';

import { NewGetHotelsV6 } from '@utils/services';

type SearchPagination = {
  stage: number;
  page: number;
  limit: number;
  exclude: number[];
};

export type HotelResponse = {
  hotels: HotelApiAlgolia[];
  hotSpotHotels: HotSpotHotelApi[];
  moreHotels: boolean;
  pagination?: SearchPagination;
  next: () => Promise<HotelResponse>;
};

export const NoopResponse: HotelResponse = {
  hotels: [],
  hotSpotHotels: [],
  moreHotels: false,
  next: async () => NoopResponse,
};

export const getDateFromString = (date: string) => {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const getCategoryByProduct = (productType: string): string | undefined =>
  Object.entries(productCategoriesMap).find(([, products]) => products.includes(productType))?.[0];

export async function GetHotelsFromApi(
  searchState: SearchState,
  pagination: SearchPagination = { stage: 1, page: 0, limit: 30, exclude: [] },
): Promise<HotelResponse> {
  const searchType = (() => {
    if (searchState.hotel_name) {
      return 'hotel';
    }
    return 'implicit';
  })();

  const onlyAvailable = searchState.filters.only_available;

  // TODO: provide an actual implementation of 'implicit' (non-ID) search by city/state/country
  // once algolia/api updates is ready
  const searchValue = searchState.hotel_name
    ? encodeURIComponent(`"${searchState.hotel_name}"`)
    : '';

  // Apply Search Filters
  const amenities = Object.entries(searchState.filters.amenities)
    .filter(([, value]) => value)
    .map(([key]) => `amenities.name:${key}`);

  const hotelStars = Object.entries(searchState.filters.hotel_stars)
    .filter(([, value]) => value)
    .map(([key]) => `hotel_star${key}`);

  const guestRatings = Object.entries(searchState.filters.guest_ratings)
    .filter(([, value]) => value)
    .map(([key]) => `rating${key}`);

  const vibes = Object.entries(searchState.filters.vibes)
    .filter(([, value]) => value)
    .flatMap(([key]) => [`vibes.primary:${key}`, `vibes.secondary:${key}`]);

  const productTypes = searchState.filters.product_types.map((value) => `product_type_id:${value}`);

  const onlyRecent = (() => {
    if (!searchState.filters.only_recent) {
      return [];
    }
    const today = new Date();
    const pad = (n: number) => (n < 10 ? `0${n}` : n.toString());
    return Array(...Array(90))
      .map((_, index) => {
        if (index === 0) return today;
        return new Date(new Date().setDate(today.getDate() - index));
      })
      .map((d) => `created_at:'${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}'`);
  })();

  const searchFilters = [
    ...(amenities.length ? [amenities] : []),
    ...(hotelStars.length ? [hotelStars] : []),
    ...(guestRatings.length ? [guestRatings] : []),
    ...(vibes.length ? [vibes] : []),
    ...(onlyRecent.length ? [onlyRecent] : []),
    ...(productTypes.length ? [productTypes] : []),
    ...(searchState.product_category
      ? [[`product_categories:"${searchState.product_category}"`]]
      : []),
  ];

  const res = await NewGetHotelsV6(
    {
      searchType,
      searchValue,
      showOnlyAvailableHotels: onlyAvailable,
      country: searchState.country_code,
      state: searchState.state_code,
      city: searchState.city,
      ...(searchState.filters.city_alias_id ? { aliasId: searchState.filters.city_alias_id } : {}),
      ...pagination,
      ...(searchState.date && searchState.date !== 'any'
        ? {
            date: getDateFromString(searchState.date),
          }
        : {}),
    },
    searchFilters,
  );

  if (res.nextPage !== undefined) {
    const nextPageParams = {
      stage: res.nextStage ?? 1,
      page: res.nextPage,
      limit: pagination.limit,
      exclude: res.nextExclude ?? [],
    };

    return {
      hotSpotHotels: res.hotSpotHotels,
      hotels: res.hotels,
      moreHotels: true,
      pagination: nextPageParams,
      next: () => GetHotelsFromApi(searchState, nextPageParams as SearchPagination),
    };
  }

  return {
    hotSpotHotels: res.hotSpotHotels,
    hotels: res.hotels,
    moreHotels: false,
    next: async () => NoopResponse,
  };
}
